/* eslint-disable react/display-name */
import React from 'react'

import moment from 'moment'
import { v4 as uuid } from 'uuid'

// react component for creating dynamic tables
import { reportResponse } from '../../layouts/Aws.js'
import PropTypes from 'prop-types'
import { CSVLink } from 'react-csv'

import { makeStyles } from '@material-ui/core/styles'
import styles from 'assets/jss/material-dashboard-pro-react/customSelectStyle.js'

import Assignment from '@material-ui/icons/Assignment'
import LinearProgress from '@material-ui/core/LinearProgress'
import Switch from '@material-ui/core/Switch'
import FormControlLabel from '@material-ui/core/FormControlLabel'

import GridContainer from '../../components/Grid/GridContainer.js'
import GridItem from '../../components/Grid/GridItem.js'
import Card from '../../components/Card/Card.js'
import CardBody from '../../components/Card/CardBody.js'
import CardIcon from '../../components/Card/CardIcon.js'
import CardHeader from '../../components/Card/CardHeader.js'
import Button from '../../components/CustomButtons/Button.js'
import AtlasGrid from '../../components/AtlasGrid/AtlasGrid.js'

import { whiteColor } from '../../assets/jss/material-dashboard-pro-react.js'

import {
  itemSalesHeadings
} from './ItemSales/ItemSalesHeadings.js'

const useStyles = makeStyles(styles)

export default function ItemSales ({ reportPeriod }) {
  const classes = useStyles()

  const [tableHeadings, setTableHeadings] = React.useState(itemSalesHeadings(false))
  const [tableData, setTableData] = React.useState([])
  const [tableDataLookup, setTableDataLookup] = React.useState({})

  const [gridLoading, setGridLoading] = React.useState(false)
  const [salesSelectionType, setSalesSelectionType] = React.useState(false)
  const [showComparison, setShowComparison] = React.useState(false)

  React.useEffect(() => {
    if (reportPeriod.startDate && reportPeriod.endDate && reportPeriod.sites) {
      setGridLoading(true)
      updateData(reportPeriod.startDate, reportPeriod.endDate, reportPeriod.sites, reportPeriod.token)
    }
  }, [reportPeriod])

  const handleSelectionType = (event, newSelection) => {
    setSalesSelectionType(newSelection)

    if (newSelection === false) {
      setTableHeadings(itemSalesHeadings(false, showComparison))
    } else {
      setTableHeadings(itemSalesHeadings(true, showComparison))
    }
  }

  const handleComparisonData = (event, newSelection) => {
    setShowComparison(newSelection)
    setGridLoading(true)

    if (newSelection === false) {
      setTableHeadings(itemSalesHeadings(salesSelectionType, false))
      setGridLoading(false)
    } else {
      setTableHeadings(itemSalesHeadings(salesSelectionType, true))
      // const end = moment(this.request.endDate, 'YYYY-MM-DD')
      const days = moment.duration(moment(reportPeriod.endDate, 'YYYY-MM-DD').diff(reportPeriod.startDate)).asDays()
      const startDate = moment(reportPeriod.comparisonDate, 'YYYY-MM-DD')
      const endDate = moment(startDate).add(days, 'days')

      reportResponse(startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'), null, reportPeriod.sites, 'daily', 'itemlevel', uuid()).then(res => {
        if (res.error || res.errorType) {
          console.log(res)
        } else {
          const data = tableData
          const lookup = tableDataLookup

          for (const prop of res.values) {
            let gross = prop[4]
            let net = prop[5]
            let profit = prop[6]
            let margin = prop[7]

            if (profit === net || profit === '0.00') {
              profit = '-'
              margin = '-'
            } else {
              profit = convertNumber(profit)
            }

            gross = convertNumber(gross)
            net = convertNumber(net)

            let ref = null

            if (prop[0] in lookup) {
              ref = data[lookup[prop[0]]]
            } else {
              data.push({
                id: data.length,
                barcode: prop[0],
                product: prop[1],
                category: prop[2],
                qty: '-',
                gross: '-',
                net: '-',
                profit: '-',
                margin: '-'
              })

              ref = data[data.length - 1]
            }

            ref.qtyc = prop[3]
            ref.grossc = gross
            ref.netc = net
            ref.profitc = profit
            ref.marginc = margin
          }

          for (const row of data) {
            if (!('qtyc' in row)) {
              row.qtyc = '-'
              row.grossc = '-'
              row.netc = '-'
              row.profitc = '-'
              row.marginc = '-'
            }
          }

          setTableData(data)
          setGridLoading(false)
        }
      })
    }
  }

  const updateData = (startDate, endDate, sites, token) => {
    setGridLoading(true)
    setShowComparison(false) // force off on reload
    setTableHeadings(itemSalesHeadings(salesSelectionType, false))
    // get last 7 days by default
    reportResponse(startDate, endDate, null, sites, 'daily', 'itemlevel', token).then(res => {
      if (res.error || res.errorType) {
        console.log(res)
      } else {
        const lookup = {}

        const data = res.values.map((prop, key) => {
          lookup[prop[0]] = key

          let gross = prop[4]
          let net = prop[5]
          let profit = prop[6]
          let margin = prop[7]

          if (profit === net || profit === '0.00') {
            profit = '-'
            margin = '-'
          } else {
            profit = convertNumber(profit)
          }

          gross = convertNumber(gross)
          net = convertNumber(net)

          return {
            id: key,
            barcode: prop[0],
            product: prop[1],
            category: prop[2],
            qty: prop[3],
            gross: gross,
            net: net,
            profit: profit,
            margin: margin
          }
        })

        setTableData(data)
        setTableDataLookup(lookup)
        setGridLoading(false)
      }
    })
  }

  const convertNumber = (number) => {
    number = Math.round(number * 100) / 100

    return number
  }

  const headers = [
    { label: 'Barcode', key: 'barcode' },
    { label: 'Product', key: 'product' },
    { label: 'Category', key: 'category' },
    { label: 'Quantity', key: 'qty' },
    { label: 'Gross', key: 'gross' },
    { label: 'Net', key: 'net' },
    { label: 'Gross Profit', key: 'profit' },
    { label: 'Gross Margin', key: 'margin' }
  ]

  return <Card style={{ marginTop: '15px' }}>
    <CardHeader color="danger" icon>
      <GridContainer direction="row" justify="space-between">
        <GridItem xs>
          <CardIcon color="danger">
            <Assignment />
          </CardIcon>
          <h4 className={classes.cardIconTitle} style={{ color: 'grey', paddingTop: '5px' }}>Item Sales</h4>
        </GridItem>
        <GridItem xs={12} sm={6} md={6} lg={6} xl={6} align="right" style={{ marginTop: '10px' }}>
          <FormControlLabel
            control={
              reportPeriod.comparisonDate
                ? <Switch
                  checked={showComparison}
                  onChange={handleComparisonData}
                  color="primary"
                  name="showComparisonData"
                  inputProps={{ 'aria-label': 'primary checkbox' }}
                />
                : <Switch
                  disabled
                  color="primary"
                  name="showComparisonData"
                  inputProps={{ 'aria-label': 'primary checkbox' }}
                />
            }
            label="Comparison Data"
            labelPlacement="start"
            style={{ marginRight: '10px' }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={salesSelectionType}
                onChange={handleSelectionType}
                color="primary"
                name="groupByCat"
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            }
            label="Group by category"
            labelPlacement="start"
            style={{ marginRight: '10px' }}
          />
          <CSVLink data={tableData} headers={headers}
            filename={`sales_${reportPeriod.startDate}_${reportPeriod.endDate}.csv`}>
            <Button color="primary" round size="sm">
              <span style={{ fontSize: '1em', color: whiteColor[0] }}>
                <i className={'fa fa-download'} />
              </span>
            </Button>
          </CSVLink>
        </GridItem>
      </GridContainer>
    </CardHeader>
    <CardBody>
      {gridLoading
        ? <div style={{ width: '100%', textAlign: 'center' }}>
          <LinearProgress />
        </div>
        : <AtlasGrid
          columnHeadings={tableHeadings}
          rowData={tableData}
          sidebar={true}
        />
      }
    </CardBody>
  </Card>
}

ItemSales.propTypes = {
  reportPeriod: PropTypes.object,
  filter: PropTypes.any,
  onChange: PropTypes.any
}
